import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Building2,
  ChevronLeft,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  FileText,
  MapPin,
  Euro,
  Maximize,
  User,
  Mail,
  Calendar,
  Download,
  AlertTriangle,
  Shield
} from 'lucide-react';

const FOUNDER_EMAIL = "rajasalame@gmail.com";

const AdminPropertiesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [properties, setProperties] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [documentUrl, setDocumentUrl] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Check if user is founder
  const isFounder = user?.email === FOUNDER_EMAIL;

  useEffect(() => {
    if (!isFounder) {
      navigate('/dashboard');
      return;
    }
    fetchProperties();
  }, [isFounder, activeTab]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/properties/admin/all`, {
        params: { validation_status: activeTab === 'all' ? null : activeTab },
        withCredentials: true
      });
      setProperties(response.data.properties || []);
      setCounts(response.data.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      toast.error('Erreur lors du chargement des biens');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocument = async (propertyId) => {
    setLoadingDocument(true);
    try {
      const response = await axios.get(`${API}/properties/admin/${propertyId}/document`, {
        withCredentials: true
      });
      setDocumentUrl(response.data.data);
    } catch (error) {
      console.error('Failed to fetch document:', error);
      toast.error('Impossible de charger le document');
      setDocumentUrl(null);
    } finally {
      setLoadingDocument(false);
    }
  };

  const handleApprove = async (propertyId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/properties/admin/${propertyId}/approve`, {}, { withCredentials: true });
      toast.success('Bien approuvé avec succès !');
      setShowDetailDialog(false);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'approbation');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedProperty) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/properties/admin/${selectedProperty.property_id}/reject`, {
        rejection_reason: rejectionReason
      }, { withCredentials: true });
      toast.success('Bien rejeté');
      setShowRejectDialog(false);
      setShowDetailDialog(false);
      setRejectionReason('');
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du rejet');
    } finally {
      setProcessing(false);
    }
  };

  const handleResetPending = async (propertyId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/properties/admin/${propertyId}/reset-pending`, {}, { withCredentials: true });
      toast.success('Bien remis en attente');
      setShowDetailDialog(false);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(false);
    }
  };

  const openPropertyDetail = async (property) => {
    setSelectedProperty(property);
    setDocumentUrl(null);
    setShowDetailDialog(true);
    if (property.authorization_document?.has_document || property.has_authorization) {
      fetchDocument(property.property_id);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-500"><Clock className="w-3 h-3 mr-1" /> En attente</Badge>;
      case 'approved':
        return <Badge className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" /> Approuvé</Badge>;
      case 'rejected':
        return <Badge className="bg-red-600"><XCircle className="w-3 h-3 mr-1" /> Rejeté</Badge>;
      default:
        return <Badge variant="outline">Inconnu</Badge>;
    }
  };

  if (!isFounder) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-[#0D47A1] hover:bg-[#0D47A1]/5">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Tableau de bord
                </Button>
              </Link>
              <Link to="/dashboard" className="hidden md:flex">
                <img src="/logo-horizontal.png" alt="10 Millions Investor" className="h-16 sm:h-20 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#0D47A1]" />
                <span className="font-semibold text-slate-800">Administration</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-[#0D47A1] to-[#1565C0] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Building2 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Validation des Biens Immobiliers</h1>
              <p className="text-blue-100">Gérez les biens soumis par les membres</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">En attente</p>
                <p className="text-3xl font-bold text-amber-800">{counts.pending}</p>
              </div>
              <Clock className="w-10 h-10 text-amber-500" />
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Approuvés</p>
                <p className="text-3xl font-bold text-green-800">{counts.approved}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700">Rejetés</p>
                <p className="text-3xl font-bold text-red-800">{counts.rejected}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-500" />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="pending" className="relative">
              En attente
              {counts.pending > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                  {counts.pending}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved">Approuvés</TabsTrigger>
            <TabsTrigger value="rejected">Rejetés</TabsTrigger>
            <TabsTrigger value="all">Tous</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="animate-pulse">
                    <div className="h-40 bg-slate-200" />
                    <CardContent className="p-4 space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-3/4" />
                      <div className="h-6 bg-slate-200 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : properties.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {activeTab === 'pending' ? 'Aucun bien en attente de validation' : 'Aucun bien dans cette catégorie'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map(prop => (
                  <Card 
                    key={prop.property_id} 
                    className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => openPropertyDetail(prop)}
                    data-testid={`admin-property-card-${prop.property_id}`}
                  >
                    {/* Image */}
                    <div className="relative h-40 bg-slate-100">
                      {prop.images?.length > 0 ? (
                        <img src={prop.images[0]} alt={prop.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className="w-12 h-12 text-slate-300" />
                        </div>
                      )}
                      <div className="absolute top-2 left-2">
                        {getStatusBadge(prop.validation_status)}
                      </div>
                      {(prop.authorization_document?.has_document || prop.has_authorization) && (
                        <div className="absolute top-2 right-2 bg-white/90 p-1.5 rounded-full">
                          <FileText className="w-4 h-4 text-green-600" />
                        </div>
                      )}
                    </div>
                    
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-lg line-clamp-1 mb-2">{prop.title}</h3>
                      
                      <p className="text-xl font-bold text-[#0D47A1] mb-2">
                        {prop.price?.toLocaleString('fr-FR')} €
                      </p>
                      
                      <div className="flex items-center text-slate-600 text-sm mb-2">
                        <MapPin className="w-4 h-4 mr-1" />
                        {prop.ville} ({prop.code_postal})
                      </div>
                      
                      <div className="flex items-center text-slate-500 text-sm mb-3">
                        <User className="w-4 h-4 mr-1" />
                        {prop.owner_name}
                      </div>
                      
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {new Date(prop.created_at).toLocaleDateString('fr-FR')}
                        </span>
                        <span className="flex items-center">
                          <Maximize className="w-3 h-3 mr-1" />
                          {prop.surface} m²
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Property Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedProperty && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{selectedProperty.title}</span>
                  {getStatusBadge(selectedProperty.validation_status)}
                </DialogTitle>
                <DialogDescription>
                  Soumis le {new Date(selectedProperty.created_at).toLocaleDateString('fr-FR')} par {selectedProperty.owner_name}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Images */}
                {selectedProperty.images?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {selectedProperty.images.map((img, i) => (
                      <img key={i} src={img} alt="" className="w-full h-24 object-cover rounded-lg" />
                    ))}
                  </div>
                )}

                {/* Property Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <Label className="text-slate-500">Prix</Label>
                      <p className="text-xl font-bold text-[#0D47A1]">{selectedProperty.price?.toLocaleString('fr-FR')} €</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Localisation</Label>
                      <p className="font-medium">{selectedProperty.ville} ({selectedProperty.code_postal})</p>
                      {selectedProperty.adresse && <p className="text-sm text-slate-600">{selectedProperty.adresse}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-500">Surface</Label>
                      <p className="font-medium">{selectedProperty.surface} m²</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-slate-500">Propriétaire</Label>
                      <p className="font-medium">{selectedProperty.owner_name}</p>
                      <p className="text-sm text-slate-600 flex items-center">
                        <Mail className="w-3 h-3 mr-1" /> {selectedProperty.owner_email}
                      </p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Catégorie</Label>
                      <p className="font-medium">{selectedProperty.category || 'Non spécifiée'}</p>
                    </div>
                    {selectedProperty.rendement_brut && (
                      <div>
                        <Label className="text-slate-500">Rendement brut estimé</Label>
                        <p className="font-medium text-green-600">{selectedProperty.rendement_brut.toFixed(2)}%</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                {selectedProperty.description && (
                  <div>
                    <Label className="text-slate-500">Description</Label>
                    <p className="mt-1 text-slate-700 whitespace-pre-wrap">{selectedProperty.description}</p>
                  </div>
                )}

                {/* Rejection reason if rejected */}
                {selectedProperty.validation_status === 'rejected' && selectedProperty.rejection_reason && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <Label className="text-red-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Raison du rejet
                    </Label>
                    <p className="mt-1 text-red-600">{selectedProperty.rejection_reason}</p>
                  </div>
                )}

                {/* Authorization Document */}
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-4">
                  <Label className="text-slate-700 font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-600" />
                    Justificatif d'autorisation de commercialisation
                  </Label>
                  
                  {loadingDocument ? (
                    <div className="mt-4 text-center py-8">
                      <div className="animate-spin w-8 h-8 border-4 border-[#0D47A1] border-t-transparent rounded-full mx-auto" />
                      <p className="mt-2 text-slate-500">Chargement du document...</p>
                    </div>
                  ) : documentUrl ? (
                    <div className="mt-4">
                      {documentUrl.startsWith('data:application/pdf') ? (
                        <div className="space-y-3">
                          <div className="bg-slate-100 rounded-lg p-4 text-center">
                            <FileText className="w-12 h-12 text-red-500 mx-auto mb-2" />
                            <p className="text-slate-600">Document PDF</p>
                          </div>
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = documentUrl;
                              link.download = `justificatif_${selectedProperty.property_id}.pdf`;
                              link.click();
                            }}
                          >
                            <Download className="w-4 h-4 mr-2" /> Télécharger le PDF
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <img 
                            src={documentUrl} 
                            alt="Justificatif" 
                            className="w-full max-h-96 object-contain rounded-lg border"
                          />
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => window.open(documentUrl, '_blank')}
                          >
                            <Eye className="w-4 h-4 mr-2" /> Ouvrir en plein écran
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-4 text-center py-8 bg-red-50 rounded-lg">
                      <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                      <p className="text-red-600">Aucun document fourni</p>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                {selectedProperty.validation_status === 'pending' && (
                  <>
                    <Button
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => setShowRejectDialog(true)}
                      disabled={processing}
                      data-testid="reject-property-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" /> Rejeter
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(selectedProperty.property_id)}
                      disabled={processing}
                      data-testid="approve-property-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Approuver
                    </Button>
                  </>
                )}
                {selectedProperty.validation_status === 'approved' && (
                  <Button
                    variant="outline"
                    onClick={() => handleResetPending(selectedProperty.property_id)}
                    disabled={processing}
                  >
                    <Clock className="w-4 h-4 mr-2" /> Remettre en attente
                  </Button>
                )}
                {selectedProperty.validation_status === 'rejected' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleResetPending(selectedProperty.property_id)}
                      disabled={processing}
                    >
                      <Clock className="w-4 h-4 mr-2" /> Remettre en attente
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(selectedProperty.property_id)}
                      disabled={processing}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Approuver
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeter ce bien</DialogTitle>
            <DialogDescription>
              Indiquez la raison du rejet. Cette information sera envoyée au propriétaire.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Raison du rejet (optionnel)</Label>
            <Textarea
              placeholder="Ex: Document d'autorisation non valide, informations incomplètes..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Annuler
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleReject}
              disabled={processing}
              data-testid="confirm-reject-btn"
            >
              {processing ? 'En cours...' : 'Confirmer le rejet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPropertiesPage;
